import { useEffect } from 'react';

import { prismicClient } from '@/data';
import { usePrismicStaleTime } from '@/data/prismicHooks';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { PromoCodeDefaultProductSyncDocument } from '@/types/prismic.generated';
import { QueryFunctionContext, useQuery, useQueryClient } from '@tanstack/react-query';

type Props = ['promo_code_default_product_sync', string | undefined];

const fetcher = async (context: QueryFunctionContext<Props>) => {
    const [key, syncUid] = context.queryKey;
    const syncDocument = await prismicClient.getByUID<PromoCodeDefaultProductSyncDocument>(key, syncUid ?? '');

    return syncDocument.data.promo_code_mappings.reduce<Record<string, string>>((acc, item) => {
        if (item.promo_code && item.vertical) {
            acc[item.promo_code] = item.vertical;
        }
        return acc;
    }, {});
};

/**
 * Returns the promo code -> vertical map used to resolve a user's default product, read from
 * the `promo_code_default_product_sync` document linked on the global configuration.
 *
 * `isLoading` is true only while the linked document is being fetched (false when no document
 * is linked), so consumers can hold off promo-based decisions until the map is available.
 */
export const usePromoCodeDefaultProductSyncMap = () => {
    const promoCodeDefaultProductSyncUid = useJurisdictionStore(
        store => store.jurisdictionSettings?.globalSettings?.promoCodeDefaultProductSyncUid
    );
    const staleTime = usePrismicStaleTime();

    const { data: syncMap, isLoading } = useQuery({
        queryKey: ['promo_code_default_product_sync', promoCodeDefaultProductSyncUid],
        queryFn: fetcher,
        enabled: !!promoCodeDefaultProductSyncUid,
        staleTime,
    });

    return { syncMap, isLoading };
};

/**
 * Warms the promo code sync map cache ahead of `DefaultProductOnboarding`, so the screen can
 * preselect the promo-code product on first render to provide the user with better UX.
 */
export const usePrefetchPromoCodeDefaultProductSyncMap = () => {
    const queryClient = useQueryClient();
    const promoCodeDefaultProductSyncUid = useJurisdictionStore(
        store => store.jurisdictionSettings?.globalSettings?.promoCodeDefaultProductSyncUid
    );
    const staleTime = usePrismicStaleTime();

    useEffect(() => {
        if (!promoCodeDefaultProductSyncUid) {
            return;
        }

        queryClient.prefetchQuery({
            queryKey: ['promo_code_default_product_sync', promoCodeDefaultProductSyncUid],
            queryFn: fetcher,
            staleTime,
        });
    }, [queryClient, promoCodeDefaultProductSyncUid, staleTime]);
};
